import React, { FunctionComponent, useEffect, useState, useCallback } from 'react';
import { Answers, CanineScoreCalculationSchema, CanineScoreCalculatorField } from '../dynamicFormSchema';
import { Label } from '../../Label';
import { DynamicFieldProps } from '../DynamicField';
import { DynamicFieldComponent } from './DynamicFieldComponentBase';
import { CanineScoreDescription } from './CanineScoreDescription';

export const MAP_COAST_TO_COMPANION_FIELD : Record<string, string[]> = {
  "dog_degree_discomfort": ["owner_reported_discomfort"],
  "static_posture": ["sit_position", "down_position", "static_posture"],
  "motion": ["rise_from_sit", "rise_from_down", "effect_on_motion"],
  "pain_on_manipulation": ["spinal_pain", "joint_pain"],
  "prom": ["joint_crepitus"]
};

const isRecordStringArray = (value: any): value is Record<string, string[]> => {
  if (typeof value !== "object" || value === null) return false;
  
  return Object.values(value).every(
    (v) => Array.isArray(v) && v.every((item) => typeof item === "string")
  );
}

// returns undefined if there are no options selected, otherwise returns the max average score of companion fields
export const getCoastScore = (
  fields: Record<string, unknown>, 
  coastFields: Record<string, string[]>
) => {

  const coastScores: (number | undefined)[] = Object.keys(coastFields).map((coastField) => {
    if (!isRecordStringArray(coastFields)) {
      throw new Error("CanineScoreCalculatorFieldComponent: CoastFields must be a record of string arrays");
    }

    // using ! to ignore the type error because we know that value is string[]
    // it will only fail fail if the type is any
    const fieldNames: string[] = coastFields[coastField]!;

    // filter out the companion fields that are undefined, null, or empty by checking fields[field]
    const companionFieldValues = fieldNames.filter((field: string) => {
     
      if (!(field in fields)) {
        throw new Error(`CanineScoreCalculatorFieldComponent ${field} is required`);
      }
      
      const value = fields[field];

      // Handle actual null/undefined values
      if (value === undefined || value === null) {
        return false;
      }
      
      // Handle string representations of null/undefined
      if (typeof value === 'string') {
        return value !== '' && value !== 'undefined' && value !== 'null';
      }
      
      // Handle other types (numbers, etc.)
      return true;
    });

    // if there are no companion fields, return undefined
    if (companionFieldValues.length === 0) {
      return undefined;
    };

    // return the average of the companionFieldValues
    const average = companionFieldValues.reduce((sum: number, fieldName: string) => 
      sum + parseInt(fields[fieldName] as string), 0) / companionFieldValues.length;
    return average;
  });

  const nonUndefinedScores = coastScores.filter((score) => score !== undefined);
  
  if (nonUndefinedScores.length === 0) { 
    return undefined;
  }

  return Math.round(Math.max(...nonUndefinedScores));
}

// CanineScoreCalculatorFieldComponent calculates and displays a canine score based on the values of other fields in
// the form.
export const CanineScoreCalculatorFieldComponent: FunctionComponent<DynamicFieldProps> = ({
  field,
  reactHookField,
  watch
}) => {
  const c = CanineScoreCalculationSchema.parse(field);
  const [score, setScore] = useState<number | undefined>(undefined);

  // Memoize the calculation function to prevent unnecessary re-renders
  const calculateScore = useCallback((fields: Record<string, unknown>) => {
    const total = getCoastScore(fields, MAP_COAST_TO_COMPANION_FIELD);
    setScore(total);
  }, []);

  useEffect(() => {
    // this prevents infinite re-renders by only running when the watch changes
    watch(calculateScore);
  }, [watch, calculateScore]); 

  useEffect(() => {
    // Update the value in reactHookField when the summarized value changes
    reactHookField.onChange(score);
  }, [score, reactHookField]);

  return (
    <div data-testid={c.name}>
      <Label description={c.description}>{c.label}</Label>
      <div className="mt-2 flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2 text-lg font-semibold">
          <CanineScoreDescription score={score} />
        </div>
      </div>
    </div>
  );
};

type CanineScoreValue = number | undefined | null;

export class DynamicCanineScoreCalculatorField extends DynamicFieldComponent<
  CanineScoreCalculatorField,
  CanineScoreValue
> {
  Component = CanineScoreCalculatorFieldComponent;

  constructor(field: CanineScoreCalculatorField) {
    super(field);
  }

  getAnswer(answers: Answers): CanineScoreValue {
    const answer = answers[this.field.name];
    if (typeof answer === 'number') {
      return answer;
    }
    return this.getDefaultValue();
  }

  getDefaultValue(): CanineScoreValue {
    return undefined;
  }

  getValueToSubmit(value: unknown): CanineScoreValue {
    if (typeof value === 'undefined') {
      return this.getDefaultValue();
    }

    return value as number;
  }
}
